import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// Phase 7 sign-off: profiles email, password.
import { MAX_LENGTHS, useCappedPaste } from '../utils/inputValidation';
import { isFieldOk, parseServerInputError } from '../utils/inputFormHelpers';

/**
 * Standalone page reached from the "Account scheduled for deletion" email.
 * Lets a user cancel a pending deletion by re-entering their credentials.
 * Calls POST /api/v1/auth/cancel-deletion (no auth required — anonymous).
 */
export default function CancelDeletionPage() {
  const { i18n } = useTranslation();
  const isKo = i18n.language === 'ko';
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const onEmailPaste    = useCappedPaste(MAX_LENGTHS.email);
  const onPasswordPaste = useCappedPaste(MAX_LENGTHS.password);
  const emailResult     = isFieldOk(email,    'email');
  const passwordResult  = isFieldOk(password, 'password');
  const submitDisabled  = busy || !emailResult.ok || !passwordResult.ok;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const res = await fetch('/api/v1/auth/cancel-deletion', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) {
        const parsed = await parseServerInputError(res, { fallback: 'request_failed' });
        throw new Error(parsed.errorCode || parsed.message || 'request_failed');
      }
      setSuccess(true);
    } catch (err) {
      setError(String(err.message || err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24, background: '#F5F5F5',
    }}>
      <div style={{
        background: '#fff', maxWidth: 480, width: '100%',
        borderRadius: 12, padding: 32, boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>
          {isKo ? '계정 복구' : 'Restore your account'}
        </h1>
        <p style={{ fontSize: 14, color: '#555', marginBottom: 20, lineHeight: 1.5 }}>
          {isKo
            ? '예약된 삭제를 취소하고 계정을 복구하려면 이메일과 비밀번호를 입력하세요. 30일이 지나면 복구할 수 없습니다.'
            : 'Enter your email and password to cancel the scheduled deletion and restore access. Recovery is not possible after 30 days.'}
        </p>

        {success ? (
          <div>
            <div style={{
              padding: 12, borderRadius: 8, background: '#ECFDF5',
              color: '#065F46', fontSize: 14, marginBottom: 16,
            }}>
              {isKo ? '계정이 복구되었습니다. 이제 로그인할 수 있습니다.' : 'Account restored. You can sign in now.'}
            </div>
            <button
              onClick={() => navigate('/login', { replace: true })}
              style={{
                width: '100%', padding: '10px 0', borderRadius: 8,
                background: '#a100aa', color: '#fff', fontWeight: 600,
                border: 0, cursor: 'pointer',
              }}
            >
              {isKo ? '로그인으로 이동' : 'Go to sign in'}
            </button>
          </div>
        ) : (
          <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <label style={{ fontSize: 13, color: '#374151' }}>
              {isKo ? '이메일' : 'Email'}
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onPaste={onEmailPaste}
                maxLength={MAX_LENGTHS.email}
                required
                autoFocus
                autoComplete="email"
                style={{
                  display: 'block', width: '100%', marginTop: 4, padding: '8px 10px',
                  border: '1px solid #ccc', borderRadius: 6, fontSize: 14,
                }}
              />
            </label>
            <label style={{ fontSize: 13, color: '#374151' }}>
              {isKo ? '비밀번호' : 'Password'}
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onPaste={onPasswordPaste}
                maxLength={MAX_LENGTHS.password}
                required
                autoComplete="current-password"
                style={{
                  display: 'block', width: '100%', marginTop: 4, padding: '8px 10px',
                  border: '1px solid #ccc', borderRadius: 6, fontSize: 14,
                }}
              />
            </label>

            {error && (
              <div style={{
                padding: 10, borderRadius: 6, background: '#FEF2F2',
                color: '#991B1B', fontSize: 13,
              }}>
                {error === 'recovery_window_expired'
                  ? (isKo ? '복구 가능 기간(30일)이 만료되었습니다.' : 'The 30-day recovery window has expired.')
                  : error === 'invalid_credentials'
                    ? (isKo ? '이메일 또는 비밀번호가 올바르지 않습니다.' : 'Email or password is incorrect.')
                    : error === 'no_pending_deletion'
                      ? (isKo ? '예약된 삭제가 없습니다.' : 'No pending deletion found.')
                      : error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitDisabled}
              style={{
                marginTop: 8, padding: '10px 0', borderRadius: 8,
                background: busy ? '#9CA3AF' : '#a100aa', color: '#fff',
                fontWeight: 600, border: 0, cursor: busy ? 'not-allowed' : 'pointer',
              }}
            >
              {busy
                ? (isKo ? '처리 중…' : 'Working…')
                : (isKo ? '계정 복구' : 'Restore account')}
            </button>

            <Link to="/login" style={{ textAlign: 'center', fontSize: 13, color: '#a100aa', marginTop: 8 }}>
              {isKo ? '로그인으로 돌아가기' : 'Back to sign in'}
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
